import {
  Avatar,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { CameraAlt } from "@mui/icons-material";
import { useState, useContext } from "react";
import axios from "axios";
import CustomSnackbar from "components/CustomSnackbar";
import DotPulseLoading from "components/Loading/DotPulseLoading";
import { useTranslation } from "react-i18next";
import { AuthContext } from "auth/AuthContext";

const ProfileAvatar = () => {
  const { user, updateUserData } = useContext(AuthContext);
  const [avatar, setAvatar] = useState(
    user?.profileImageUrl || "/assets/img/default-avatar.jpg"
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Optimistic preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setAvatar(event.target.result);
      };
      reader.readAsDataURL(file);
      setSelectedFile(file);
      // Auto-upload immediately
      handleUpload(file);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleUpload = async (file = selectedFile) => {
    if (!file || !user) return;

    const formData = new FormData();
    formData.append("file", file);

    const originalAvatar = user.profileImageUrl || "/assets/img/default-avatar.jpg";
    let uploadedImageUrl = null;

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Unauthorized: No token found");
      }
      setIsUploading(true);

      // Upload file
      const uploadRes = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      uploadedImageUrl = uploadRes.data.url;
      if (!uploadedImageUrl) {
        throw new Error("Upload failed: No URL returned");
      }

      // Optimistically set avatar
      setAvatar(uploadedImageUrl);

      const lang = localStorage.getItem("i18nextLng") || "en";
      const payload = {
        profileImageUrl: uploadedImageUrl,
      };

      // Update user profile
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/auth/profile`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": lang,
          },
        }
      );

      // Update context and localStorage
      updateUserData({ profileImageUrl: uploadedImageUrl });

      setSelectedFile(null);
      setSnackbar({
        open: true,
        message: "Photo Profile updated successfully.",
        severity: "success",
      });
    } catch (error) {
      // Rollback on error
      setAvatar(originalAvatar);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || "Upload failed",
        severity: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "center", sm: "flex-start" },
          mb: 4,
          gap: 3,
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Avatar
            src={avatar}
            sx={{
              width: { xs: 80, sm: 120 },
              height: { xs: 80, sm: 120 },
              border: "4px solid #fff",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              transition: "transform 0.3s ease",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          />
          <IconButton
            component="label"
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              bgcolor: "primary.main",
              color: "white",
              "&:hover": { bgcolor: "primary.dark" },
              transform: { xs: "scale(0.8)", sm: "scale(1)" },
            }}
          >
            <CameraAlt />
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleAvatarChange}
            />
          </IconButton>
        </Box>
        {isUploading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
            <CircularProgress size={20} thickness={4} />
            {t("Dashboard_profile.upload.uploading")}
          </Box>
        )}
      </Box>

      {/* Modern Snackbar for notifications */}
      <CustomSnackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />

      {isUploading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DotPulseLoading size={14} color="#3f51b5" />
        </Box>
      )}
    </>
  );
};

export default ProfileAvatar;
